import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Experience, Content } from '../hooks/api/useExperiences';
import { AudioPauseIcon, AudioPlayIcon } from '../assets/images/svgs';
import { EventLocations, useHubspotEventMutation } from '../hooks/api/useEvent';
import useAuth from '../hooks/utils/useAuth';
import { TopicTag } from './topic-tag';
import { useAudioPlayer } from '../hooks/utils/useAudioPlayer';
import { useTagGroups } from '../hooks/api/useTagGroups';
import { ROUTE_PATH } from '../routes/route-paths';
import { usePeersona } from '../contexts/peersona-context';
import { usePeersonaChatBot } from '../hooks/api/usePeersonaChat';
import { PeersonaAvatar } from './peersona/peersona-avatar';
import { PeersonaSparkle } from './peersona/peersona-sparkle';
import { PEERSONA_GRADIENT_FROM, PEERSONA_GRADIENT_TO } from './peersona/peersona-styles';
import { usePeersonaAccess } from '../hooks/utils/usePeersonaAccess';
import { AffiliationBanner, getAffiliationClientName } from './affiliation-banner';
import { ExperienceMatchHint, MatchStyleVariant } from './peer-listener-match-hint';

interface AudioExperienceTileProps {
  experience?: Experience | Content;
  handleScheduleModalOpen?: (experience: Experience | Content) => void;
  selectedTagGroupName?: string;
  isLoading?: boolean;
  /** Fired when the user starts playback from this tile (e.g. Fi analytics / follow-up). */
  onTileEngage?: () => void;
  showTopicTag?: boolean;
  /**
   * If this audio's peer has an active Peersona, pass the chatbot id here to
   * surface a "Talk with <peer>" prompt when playback finishes. The tile only
   * renders the overlay on the audio's natural end — it does NOT fire on
   * pause, seek, or replay. Other surfaces (Fi, search, browse) typically
   * don't have this loaded and should leave it undefined.
   */
  peersonaChatbotId?: number | null;
  /** Allow playback without an authenticated user (public collection pages). */
  allowAnonymousPlayback?: boolean;
  matchStyle?: MatchStyleVariant;
}

export const AudioExperienceTileSkeleton: React.FC = () => {
  return (
    <div className="inline-flex w-[194px] flex-col items-start justify-start gap-2">
      <div className="relative h-[318px] w-[194px] animate-pulse overflow-hidden rounded-xl bg-gray-200" />
      <div className="h-6 w-32 animate-pulse rounded bg-gray-200" />
      <div className="h-10 w-full animate-pulse rounded bg-gray-200" />
    </div>
  );
};

const getDisplayTagGroup = (tagGroupNames: string[], selectedTagGroupName?: string): string | undefined => {
  if (!tagGroupNames?.length) {
    return undefined;
  }
  if (selectedTagGroupName && tagGroupNames.includes(selectedTagGroupName)) {
    return selectedTagGroupName;
  }
  return tagGroupNames[0];
};

export const AudioExperienceTile: React.FC<AudioExperienceTileProps> = ({
  experience,
  handleScheduleModalOpen,
  selectedTagGroupName,
  isLoading = false,
  onTileEngage,
  showTopicTag = true,
  peersonaChatbotId,
  allowAnonymousPlayback = false,
  matchStyle = 'none',
}) => {
  const history = useHistory();
  const onTileEngageRef = useRef(onTileEngage);
  onTileEngageRef.current = onTileEngage;
  const { user } = useAuth();
  const hubspotMutation = useHubspotEventMutation();
  const { data: topicTagGroups } = useTagGroups();
  const { openPanelForChatbot } = usePeersona();
  // Only fetch the ChatBot meta when we actually have a chatbot to talk to.
  // The query is React-Query-deduped, so multiple audio tiles for the same
  // peer share a single network call.
  const { data: peersonaChatbot } = usePeersonaChatBot(peersonaChatbotId ?? null);

  const { isPeerPeersonaAllowlisted } = usePeersonaAccess();
  const tilePeerListenerRoleId =
    (experience as Experience | undefined)?.listener_role_id ??
    (experience as Content | undefined)?.listener_role_id ??
    null;
  const showPeersonaSparkle = isPeerPeersonaAllowlisted(tilePeerListenerRoleId);

  const dummyExperience = useMemo(() => ({ id: 0, listener_role_id: 0, rating: 0, duration: 0 }) as Experience, []);

  const { audioRef, paused, percent, audioStopAll, onPlay, onPause, onEnded, onTimeUpdate } = useAudioPlayer(
    experience || dummyExperience,
    'search-results',
  );

  // Post-audio Peersona prompt state. Reset whenever the user starts playing
  // again so a replay can re-prompt (the natural "watch then talk" cadence).
  const [showPeersonaPrompt, setShowPeersonaPrompt] = useState(false);

  const handleAudioEnded = useCallback(() => {
    // Always run the player's cleanup first so state stays in sync.
    onEnded();
    if (peersonaChatbotId) {
      setShowPeersonaPrompt(true);
    }
  }, [onEnded, peersonaChatbotId]);

  const handleOpenPeersona = useCallback(() => {
    if (!peersonaChatbotId) return;
    setShowPeersonaPrompt(false);
    openPanelForChatbot(peersonaChatbotId);
  }, [peersonaChatbotId, openPanelForChatbot]);

  const handleDismissPrompt = useCallback((event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation();
    setShowPeersonaPrompt(false);
  }, []);

  const tagGroupNames = useMemo(
    () => (experience as Content)?.tag_group_names || (experience as Experience)?.tag_group_names || [],
    [experience],
  );
  const displayTagGroup = useMemo(
    () => getDisplayTagGroup(tagGroupNames, selectedTagGroupName),
    [tagGroupNames, selectedTagGroupName],
  );
  const title = useMemo(
    () => experience?.ai_title || experience?.title || `${experience?.name}'s experience`,
    [experience?.ai_title, experience?.title, experience?.name],
  );

  const handlePlayPauseClick = useCallback(() => {
    if (!experience) return;
    if (!user && !allowAnonymousPlayback) return;

    if (paused) {
      if (user) {
        hubspotMutation.mutate({
          event: 'click_experience_tile',
          properties: {
            client: user.client_name,
            email_address: user.email_address,
            experience_id: experience.id,
            location: EventLocations.SEARCH,
            media_type: 'audio',
          },
        });
      }
      onTileEngageRef.current?.();
      audioStopAll?.();
      // Hide any lingering post-audio prompt when restarting playback so the
      // user can re-trigger it cleanly on the next natural completion.
      setShowPeersonaPrompt(false);
      onPlay();
    } else {
      onPause();
    }
  }, [user, allowAnonymousPlayback, hubspotMutation, experience, paused, audioStopAll, onPlay, onPause]);

  const handlePeerClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!experience) return;
      if (!user && allowAnonymousPlayback) {
        handleScheduleModalOpen?.(experience);
        return;
      }
      const listenerRoleId = (experience as Experience).listener_role_id ?? (experience as Content).listener_role_id;
      if (!listenerRoleId) return;
      history.push(`${ROUTE_PATH.PEER_DETAILS}?listenerId=${listenerRoleId}`);
    },
    [experience, user, allowAnonymousPlayback, handleScheduleModalOpen, history],
  );

  const handleSeek = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!audioRef.current) return;
      const audio = audioRef.current;
      if (!audio.duration || Number.isNaN(audio.duration)) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const nextPercent = Math.max(0, Math.min(1, clickX / rect.width));
      audio.currentTime = nextPercent * audio.duration;
    },
    [audioRef],
  );

  const audioSrc = experience
    ? (experience as Content).file_path || (experience as Experience).audio_url || null
    : null;

  const handleTileActivate = useCallback(() => {
    if (!audioSrc) return;
    handlePlayPauseClick();
  }, [audioSrc, handlePlayPauseClick]);

  if (isLoading || !experience) {
    return <AudioExperienceTileSkeleton />;
  }

  const thumbnailUrl =
    experience.profile_photo_square_file_url ||
    (experience as any).vimeo_thumbnail_url ||
    (experience as any).image_url ||
    null;

  const affiliationName = getAffiliationClientName(experience, user?.client_id);
  // Match Figma Listen tiles: photo fills the top of the 318px card; ~70px gray
  // control strip at the bottom (speaker sits on the photo/strip boundary).
  const photoHeightClass = 'h-[248px]';

  return (
    <>
      <div
        className="inline-flex w-[194px] cursor-pointer flex-col items-start justify-start gap-2"
        role="button"
        tabIndex={0}
        onClick={handleTileActivate}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleTileActivate();
          }
        }}
        aria-label={paused ? 'Play audio experience' : 'Pause audio experience'}
      >
        <div className="relative isolate h-[318px] w-[194px] overflow-hidden rounded-xl bg-[rgba(114,117,136,0.15)]">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={`Audio thumbnail for ${title}`}
              className={`absolute inset-x-0 top-0 w-full object-cover ${photoHeightClass}`}
            />
          ) : (
            <div
              className={`absolute inset-x-0 top-0 flex w-full items-center justify-center bg-gray-300 ${photoHeightClass}`}
            >
              <div className="font-open-sans px-2 text-center text-sm text-gray-500">Audio thumbnail unavailable</div>
            </div>
          )}

          {showPeersonaPrompt && peersonaChatbotId && (
            <PostAudioPeersonaOverlay
              peerName={experience?.name || peersonaChatbot?.display_name || peersonaChatbot?.name || 'this peer'}
              scopeLine={peersonaChatbot?.scope_line ?? null}
              avatarUrl={peersonaChatbot?.avatar_file_path ?? thumbnailUrl ?? null}
              onAccept={handleOpenPeersona}
              onDismiss={handleDismissPrompt}
            />
          )}

          {affiliationName && <AffiliationBanner clientName={affiliationName} />}

          {experience?.name && (
            <button
              type="button"
              onClick={handlePeerClick}
              disabled={!(experience as Experience).listener_role_id && !(experience as Content).listener_role_id}
              className={`absolute left-3 z-10 flex h-6 items-center justify-center gap-1 rounded-[5px] px-3 transition-opacity hover:opacity-90 disabled:cursor-default disabled:hover:opacity-100 ${
                affiliationName ? 'top-9 bg-[rgb(var(--color-primary))]' : 'top-3 bg-white/80'
              }`}
              aria-label={`View ${experience.name}'s profile${showPeersonaSparkle ? ' (Peersona)' : ''}`}
            >
              {showPeersonaSparkle && (
                <PeersonaSparkle size={12} className={affiliationName ? 'text-white' : 'text-[#6B6FFF]'} />
              )}
              <div
                className={`font-albert-sans text-[14px] font-medium leading-[18px] ${
                  affiliationName ? 'text-white' : 'text-primary'
                }`}
              >
                {experience.name}
              </div>
            </button>
          )}

          {/* Bottom control strip — speaker sits on the photo / gray boundary like Figma */}
          <div className="absolute inset-x-0 bottom-0 flex h-[70px] items-center gap-2 px-3">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handlePlayPauseClick();
              }}
              className="relative z-10 -mt-6 flex h-12 w-12 shrink-0 items-center justify-center transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              aria-label={paused ? 'Play audio' : 'Pause audio'}
              disabled={!audioSrc}
            >
              <img src={paused ? AudioPlayIcon : AudioPauseIcon} alt="" className="h-12 w-12" />
            </button>

            <div
              className={`relative h-2 overflow-hidden rounded transition-all duration-300 ease-in-out ${
                paused ? 'w-0 opacity-0' : 'flex-1 opacity-100'
              } bg-neutral-300`}
              onClick={(e) => {
                e.stopPropagation();
                handleSeek(e);
              }}
              role="button"
              tabIndex={0}
              aria-label="Seek audio playback"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSeek(e as any);
                }
              }}
            >
              <div
                className="bg-icon-slate absolute inset-y-0 left-0 transition-[width] duration-150"
                style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
              />
            </div>
          </div>
        </div>

        {showTopicTag && displayTagGroup ? (
          <TopicTag tagGroupName={displayTagGroup} tagGroups={topicTagGroups} />
        ) : null}

        <div className="font-open-sans line-clamp-2 w-full self-stretch text-base font-semibold leading-[22px] text-black">
          {title}
        </div>
        {experience ? <ExperienceMatchHint match={experience as Content} variant={matchStyle} /> : null}

        {audioSrc && (
          <audio
            ref={audioRef}
            src={audioSrc}
            onTimeUpdate={(e) => {
              if (!e.currentTarget.paused && e.currentTarget.currentTime > 0) {
                onTimeUpdate(e);
              }
            }}
            onEnded={handleAudioEnded}
            className="hidden"
          />
        )}
      </div>
    </>
  );
};

interface PostAudioPeersonaOverlayProps {
  peerName: string;
  scopeLine: string | null;
  avatarUrl: string | null;
  onAccept: () => void;
  onDismiss: (event: React.MouseEvent | React.KeyboardEvent) => void;
}

/**
 * The "watch then talk" prompt rendered on top of an audio tile's thumbnail
 * the moment playback finishes naturally. Click anywhere except the dismiss
 * X to open the peer's Peersona panel. Kept inline in this file because it
 * has no use outside the audio tile (and depends on the tile's tight 194px
 * geometry).
 */
const PostAudioPeersonaOverlay: React.FC<PostAudioPeersonaOverlayProps> = ({
  peerName,
  scopeLine,
  avatarUrl,
  onAccept,
  onDismiss,
}) => {
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onAccept();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        onDismiss(event);
      }
    },
    [onAccept, onDismiss],
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onAccept();
      }}
      onKeyDown={handleKeyDown}
      aria-label={`Talk with ${peerName}'s Peersona about what you just heard`}
      className="absolute left-0 right-0 top-[51px] flex h-[194px] flex-col items-stretch justify-between p-3 text-left"
      style={{
        background: `linear-gradient(160deg, ${PEERSONA_GRADIENT_FROM}F2 0%, ${PEERSONA_GRADIENT_TO}F2 100%)`,
        color: 'white',
      }}
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss Peersona prompt"
        className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full border-0 bg-white/15 p-0 text-white transition-colors hover:bg-white/25"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M18 6L6 18M6 6l12 12"
            stroke="currentColor"
            strokeWidth="2.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div className="flex items-center gap-2 pr-7">
        <PeersonaAvatar size={28} src={avatarUrl} name={peerName} />
        <div className="min-w-0">
          <p className="font-open-sans text-[10px] font-semibold uppercase tracking-[0.12em] text-white/80">Peersona</p>
          <p className="font-albert-sans truncate text-sm font-semibold leading-tight text-white">
            Keep talking with {peerName}
          </p>
        </div>
      </div>

      <p className="font-open-sans line-clamp-3 text-[12px] leading-[16px] text-white/90">
        {scopeLine?.trim() ||
          `Share what came up for you while listening — ${peerName}'s Peersona can pick up where the story left off.`}
      </p>

      <span className="font-open-sans inline-flex w-fit items-center gap-1 self-start rounded-full bg-white/20 px-2.5 py-1 text-[12px] font-semibold text-white">
        Start a conversation
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M5 12h14M13 5l7 7-7 7"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </div>
  );
};
